import type {RegistrationStatus} from 'common/vendor-registration/types';

import {CheckCircleFilled, ClockCircleFilled} from '@ant-design/icons';
import {Button, Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {toast} from '@Toast';
import logo from 'assets/icons/login/logo.svg';
import {getRegistrationRoute} from 'common/vendor-registration/utils';
import RegistrationOverviewSteps from 'modules/VendorRegisterOverview/components/RegistrationOverviewSteps/RegistrationOverviewSteps';
import UnsuccessfulRegistration from 'modules/VendorRegisterOverview/components/UnsuccessfulRegistration/UnsuccessfulRegistration';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {getRegisterStatus, identityVerification} from 'store/slices/vendor-registration/thunk';

import {REGISTRATION_STATUS_INDEX} from './constants';

const VendorRegisterOverview: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isStatusResolved, setIsStatusResolved] = useState(false);
  const {registerStatus, loading} = useAppSelector((state) => state.vendorRegistration);
  const status = registerStatus as RegistrationStatus;
  const currentStepIndex = REGISTRATION_STATUS_INDEX[String(status.step).toLowerCase()] ?? 0;
  const isApprovedByAdmin =
    status.step === 'KycIdentityInfo' ||
    currentStepIndex > 6 ||
    (status.step === 'WaitingForAdminApprove' && status.state === 'APPROVED');
  const isWaitingForApproval =
    status.step === 'WaitingForAdminApprove' && status.state !== 'APPROVED';
  const isReadyForKyc = status.step === 'WaitingForAdminApprove' && status.state === 'APPROVED';
  const canContinue =
    status.step === 'Register' || status.step === 'KycIdentityInfo' || isReadyForKyc;

  useEffect(() => {
    let cancelled = false;

    void dispatch(getRegisterStatus())
      .unwrap()
      .then((response: RegistrationStatus) => {
        if (cancelled) {
          return;
        }

        const targetRoute = getRegistrationRoute(response);

        if (targetRoute === '/user-register') {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('isVendor');
          toast('امکان ثبت نام برای این شماره وجود ندارد.');
          navigate(targetRoute, {replace: true});
          return;
        }

        if (targetRoute === '/vendor-register') {
          navigate(targetRoute, {replace: true});
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast('خطا در دریافت وضعیت ثبت نام');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsStatusResolved(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate]);

  const handleContinue = async () => {
    if (!status.step || (status.step === 'WaitingForAdminApprove' && !isReadyForKyc)) {
      return;
    }

    const shouldCallKyc =
      isReadyForKyc || (status.step === 'KycIdentityInfo' && status.state !== 'APPROVED');

    if (!shouldCallKyc) {
      navigate('/vendor-register');
      return;
    }

    try {
      const response = await dispatch(identityVerification()).unwrap();

      if (response.isSuccess) {
        navigate('/vendor-register');
      } else {
        toast(response.message || 'خطا');
      }
    } catch (error) {
      toast(
        typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof error.message === 'string'
          ? error.message
          : 'خطا'
      );
    }
  };

  if (!isStatusResolved || loading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center" role="status">
        <div className="size-15">
          <Loading size="medium" />
        </div>
      </div>
    );
  }

  if (status.step === 'WaitingForAdminApprove' && status.state === 'CANCELED') {
    return <UnsuccessfulRegistration />;
  }

  return (
    <PageLayout
      footerPosition="static"
      header={
        <header className="flex h-25 items-center justify-center border-b border-gray-03">
          <img src={logo} alt="نما" />
        </header>
      }
      footer={
        status.step !== 'NotRegistered' && status.step ? (
          <Button fullWidth onClick={() => void handleContinue()} disabled={!canContinue}>
            {isWaitingForApproval
              ? 'در حال بررسی'
              : canContinue
                ? isApprovedByAdmin
                  ? 'ادامه'
                  : 'شروع کنید'
                : 'وضعیت نامعتبر'}
          </Button>
        ) : undefined
      }
    >
      <div>
        <h1 className="px-4 py-2.5 text-subtitle-md text-neutral-fg">
          {isWaitingForApproval ? 'بررسی اطلاعات...' : 'مراحل ثبت نام'}
        </h1>

        {(status.step === 'KycIdentityInfo' && status.state === 'APPROVED') || isReadyForKyc ? (
          <div className="mb-6 px-4">
            <div className="flex items-center gap-2 rounded-xl bg-gray-03 px-4 py-3">
              <CheckCircleFilled aria-hidden className="shrink-0 text-body-lg text-success" />
              <p className="text-body-sm text-neutral-fg">
                اطلاعات واردشده تأیید شد. لطفاً با انتخاب دکمه «ادامه»، فرایند احراز هویت را تکمیل
                کنید.
              </p>
            </div>
          </div>
        ) : isWaitingForApproval ? (
          <div className="mb-6 px-4">
            <div className="flex items-center gap-2 rounded-xl bg-gray-03 px-4 py-3">
              <ClockCircleFilled aria-hidden className="shrink-0 text-body-lg text-neutral-fg" />
              <p className="text-body-sm text-neutral-fg">
                اطلاعات شما با موفقیت ثبت شد و در حال بررسی است. نتیجه حداکثر تا ۲۴ ساعت آینده اعلام
                خواهد شد.
              </p>
            </div>
          </div>
        ) : null}

        <RegistrationOverviewSteps
          registrationStep={status.step}
          isApprovedByAdmin={isApprovedByAdmin}
        />
      </div>
    </PageLayout>
  );
};

export default VendorRegisterOverview;
